import { faSave } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { PickerValue } from "@mui/x-date-pickers/internals";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { FormProvider, FormState, useForm } from "../../../components/FormProvider";
import { DailyInspection } from "../../../models";
import { useSaveDailyInspectionMutation } from "../../../redux/apiStore";

export function DailyInspectionEditView(props: { dailyInspection: DailyInspection | undefined; selectedDate: Dayjs; isClosed: boolean; onBack: () => void }) {
  const { dailyInspection, selectedDate, isClosed, onBack } = props;
  const [saveDailyInspection] = useSaveDailyInspectionMutation();

  const [inspectorName, setInspectorName] = useState<string>(dailyInspection?.inspectorName?.trim() ? dailyInspection.inspectorName : "Debbie Burke");
  const [completedAt, setCompletedAt] = useState<Dayjs | null>(
    dailyInspection?.inspectionCompletedCentralDateTime && dayjs(dailyInspection.inspectionCompletedCentralDateTime).year() > 2000
      ? dayjs(dailyInspection.inspectionCompletedCentralDateTime)
      : dayjs(),
  );

  const handleSave = async (form: FormState) => {
    form.setLoading(true);
    try {
      const record: DailyInspection = {
        ...(dailyInspection ?? {
          id: 0,
          deleted: false,
          createdCentralDateTime: "2000-01-01", // api sets this on save
          modifiedCentralDateTime: "2000-01-01", // api sets this on save
        }),
        inspectionCentralDateTime: selectedDate.format("YYYY-MM-DDTHH:mm:ss"),
        inspectorName,
        inspectionCompletedCentralDateTime: completedAt?.isValid() ? completedAt.format("YYYY-MM-DDTHH:mm:ss") : selectedDate.format("YYYY-MM-DDTHH:mm:ss"),
        isClosed,
      };
      await saveDailyInspection(record).unwrap();
      form.notifySuccess("Daily inspection saved");
      onBack();
    } catch (err) {
      form.notifyError("Failed to save daily inspection", err);
    } finally {
      form.setLoading(false);
    }
  };

  const handleCancel = (_form: FormState) => {
    onBack();
  };

  return (
    <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={() => {}} showControls={false}>
      <InspectionForm
        inspectorName={inspectorName}
        setInspectorName={setInspectorName}
        completedAt={completedAt}
        setCompletedAt={setCompletedAt}
        selectedDate={selectedDate}
      />
    </FormProvider>
  );
}

function InspectionForm(props: {
  inspectorName: string;
  setInspectorName: (v: string) => void;
  completedAt: Dayjs | null;
  setCompletedAt: (v: Dayjs | null) => void;
  selectedDate: Dayjs;
}) {
  const { inspectorName, setInspectorName, completedAt, setCompletedAt, selectedDate } = props;
  const form = useForm();

  const handleCompletedAtChange = (value: PickerValue) => {
    setCompletedAt(value ? dayjs(value) : null);
  };

  return (
    <Stack gap={2} sx={{ flexGrow: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {selectedDate.format("MMMM D, YYYY")}
      </Typography>

      <TextField
        label="Inspector Name"
        variant="filled"
        value={inspectorName}
        onChange={(e) => setInspectorName(e.target.value)}
        disabled={form.isDisabled}
        required
        fullWidth
      />

      <DateTimePicker
        label="Completed Date & Time"
        value={completedAt}
        onChange={handleCompletedAtChange}
        disabled={form.isDisabled}
        slotProps={{
          textField: {
            variant: "filled",
            fullWidth: true,
          },
          field: {
            clearable: true,
          },
          popper: {
            sx: { zIndex: 1600 },
          },
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Button variant="contained" onClick={form.save} sx={{ height: 50, width: { xs: "100%", md: "inherit" } }} startIcon={<FontAwesomeIcon icon={faSave} />}>
        Save Changes
      </Button>
      <Button variant="outlined" onClick={form.cancel} sx={{ height: 50, width: { xs: "100%", md: "inherit" } }}>
        Cancel
      </Button>
    </Stack>
  );
}
